'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './CategoryFilterDropdown.module.css';
import type { StudyCategorySlug } from 'content/science/studyTypes';

/* Category filter — dropdown style.
 *
 * Trigger shows the currently selected option + a chevron. Click opens a menu
 * of every category that has items in the dataset. Click outside or Escape to
 * close, with focus returning to the trigger.
 *
 * Generic over the slug so the three callers can each speak their own
 * vocabulary — research categories, accuracy measurement areas, Ask subjects —
 * without a near-identical copy per section.
 *
 * Two color variants:
 *   - 'light' for white-papers (light bg, dark text)
 *   - 'dark'  for snapshots    (dark bg, light text) */

export interface FilterOption<T extends string = StudyCategorySlug | 'all'> {
  slug: T;
  label: string;
  count: number;
}

interface Props<T extends string> {
  active: T;
  options: FilterOption<T>[];
  onChange: (slug: T) => void;
  variant?: 'light' | 'dark';
  /* Which edge the menu hangs from. 'responsive' is left-aligned on a phone
     and right-aligned from 769px up, for a trigger that moves across the row
     at that breakpoint. */
  align?: 'left' | 'right' | 'responsive';
}

const CategoryFilterDropdown = <T extends string>({
  active,
  options,
  onChange,
  variant = 'light',
  align = 'right',
}: Props<T>) => {
  const t = useTranslations('science');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const activeOpt = options.find((o) => o.slug === active) || options[0];

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  /* Close on click outside / Escape. Escape puts the cursor back on the
     trigger — otherwise dismissing the menu strands a keyboard user at the
     top of the document. */
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* Opening moves the cursor into the menu, onto whatever is selected. */
  useEffect(() => {
    if (!open) return;
    const i = Math.max(
      0,
      options.findIndex((o) => o.slug === active),
    );
    itemRefs.current[i]?.focus();
  }, [open, active, options]);

  const onItemKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = options.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    itemRefs.current[next]?.focus();
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={styles.trigger}
        data-variant={variant}
        data-open={open ? 'true' : 'false'}
      >
        <span>{activeOpt?.label ?? t('science.filter.all')}</span>
        <span className={styles.triggerCount}>{activeOpt?.count ?? 0}</span>
        <span
          className={styles.caret}
          data-open={open ? 'true' : 'false'}
          aria-hidden="true"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 3.5L5 6.5L8 3.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          className={styles.menu}
          id={menuId}
          role="listbox"
          aria-label={t('science.filter.byCategory')}
          data-variant={variant}
          data-align={align}
        >
          {options.map((opt, i) => {
            const isActive = opt.slug === active;
            return (
              <button
                key={opt.slug}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="option"
                aria-selected={isActive}
                className={styles.item}
                data-active={isActive ? 'true' : 'false'}
                data-variant={variant}
                onKeyDown={(e) => onItemKeyDown(e, i)}
                onClick={() => {
                  onChange(opt.slug);
                  close(true);
                }}
              >
                <span>{opt.label}</span>
                <span className={styles.itemCount}>{opt.count}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default CategoryFilterDropdown;

export { buildFilterOptions } from './CategoryFilterChips';
